import {
  isComponentElement,
  isTemplateElement,
} from 'features/unifiedFlow/api/interfaces'
import { getAllComponentElements } from 'features/unifiedFlow/contentPage/helpers'

import { DuplicateWorkflowModalElement } from './DuplicateWorkflowModal'

type ContentElementDetails = {
  ownSubjectLine?: string
  elementType?: string
  optimisationType?: string
  displayName?: string
  templateName?: string
  projectId?: string
  groupName?: string
}

// Elements nested inside a template/multi-step grouping (e.g. "Marketing email")
// carry that group's display name, so the modal can show it as a section header
// above the group's own controllable fields.
const getGroupNameByElementId = (content: any): Map<number, string> => {
  const groupNameByElementId = new Map<number, string>()

  content?.elements?.forEach((element: any) => {
    if (isTemplateElement(element)) {
      const groupName = element?.display_name ?? element?.name
      if (!groupName) {
        return
      }
      element.elements?.filter(isComponentElement).forEach((child: any) => {
        if (typeof child?.element_id === 'number') {
          groupNameByElementId.set(child.element_id, groupName)
        }
      })
    }
  })

  return groupNameByElementId
}

type DuplicatePreparationResult = {
  isPersonaliseWorkflow: boolean
  allowHumanControlEdit: boolean
  canIncludeGeneratedContent: boolean
  templatesWithoutGeneratedContent: string[]
  elementDetailsById: Map<number, ContentElementDetails>
}

const isPersonalizeElement = (element: any) =>
  element?.personalization_settings?.type === 'personalize'

const isOptimizeElement = (element: any) =>
  element?.optimization_settings?.type === 'optimize'

const isContentOrOptimizeElement = (element: any) =>
  element?.optimization_settings?.type === 'content' ||
  isOptimizeElement(element)

const hasCloneableGeneratedVariant = (element: any) =>
  element?.variants?.some(
    (variant: any) => variant?.status === 'active' && !variant?.ownsl
  )

const hasExplicitOptimizationType = (element: any) =>
  Boolean(element?.optimization_settings?.type)

const isEligibleForGeneratedDuplication = (element: any) => {
  if (isPersonalizeElement(element)) {
    return false
  }

  // If the element is not an optimize element but is a content element.
  if (!hasExplicitOptimizationType(element)) {
    return hasCloneableGeneratedVariant(element)
  }

  return isContentOrOptimizeElement(element)
}

export const getDuplicatePreparation = (
  content: any
): DuplicatePreparationResult => {
  // Flattens template/multi-step groupings (e.g. "Marketing email") into their
  // leaf component elements, so control lines for grouped elements are included too.
  const elements = getAllComponentElements(content)
  const isPersonaliseWorkflow = Boolean(
    elements.some((element: any) => isPersonalizeElement(element))
  )
  const isOptimizeWorkflow = Boolean(
    elements.some((element: any) => isOptimizeElement(element))
  )
  const canIncludeGeneratedContent = Boolean(
    elements.some(
      (element: any) =>
        isEligibleForGeneratedDuplication(element) &&
        hasCloneableGeneratedVariant(element)
    )
  )

  const templatesWithoutGeneratedContent = elements.reduce<string[]>(
    (accumulator, element: any) => {
      if (!isEligibleForGeneratedDuplication(element)) {
        return accumulator
      }

      if (hasCloneableGeneratedVariant(element)) {
        return accumulator
      }

      const templateName = element?.display_name?.trim()
      if (templateName) {
        accumulator.push(templateName)
      }

      return accumulator
    },
    []
  )

  const groupNameByElementId = getGroupNameByElementId(content)
  const elementDetailsById = new Map<number, ContentElementDetails>()
  elements.forEach((element: any) => {
    const elementId = element.element_id
    const ownSubjectLine =
      element?.optimization_settings?.configuration?.own_subject_line ??
      element?.personalization_settings?.configuration?.own_subject_line ??
      ''
    const projectId =
      element?.optimization_settings?.configuration?.project_id ??
      element?.personalization_settings?.configuration?.project_id

    if (typeof elementId === 'number') {
      elementDetailsById.set(elementId, {
        ownSubjectLine,
        elementType: element.element_type,
        optimisationType:
          element?.optimization_settings?.type ??
          element?.personalization_settings?.type,
        displayName: element?.display_name ?? element?.name,
        templateName: element?.template_name,
        projectId,
        groupName: groupNameByElementId.get(elementId),
      })
    }
  })

  return {
    isPersonaliseWorkflow,
    allowHumanControlEdit: isPersonaliseWorkflow || isOptimizeWorkflow,
    canIncludeGeneratedContent,
    templatesWithoutGeneratedContent,
    elementDetailsById,
  }
}

// elementDetailsById is the authoritative set of leaf/controllable elements
// (it already walks into template groupings), so it drives the result rather
// than just enriching `elements` in place - otherwise elements nested inside a
// template grouping (which never appear in the initial top-level `elements`
// seed) would be silently dropped instead of surfaced as their own rows.
export const mergeElementDetailsIntoDuplicateModalElements = (
  elements: DuplicateWorkflowModalElement[],
  elementDetailsById: Map<number, ContentElementDetails>
): DuplicateWorkflowModalElement[] => {
  const elementsById = new Map(
    elements.map((element) => [element.elementId, element])
  )

  return Array.from(elementDetailsById.entries()).map(
    ([elementId, details]) => {
      const existing = elementsById.get(elementId)
      return {
        ...existing,
        elementId,
        ownSubjectLine: details.ownSubjectLine,
        elementType: details.elementType,
        optimisationType: details.optimisationType,
        displayName: details.displayName,
        templateName: details.templateName ?? existing?.templateName,
        projectId: details.projectId ?? existing?.projectId,
        groupName: details.groupName,
      }
    }
  )
}

export const getElementModeLabel = (
  element: DuplicateWorkflowModalElement
): 'OPTIMISE' | 'PERSONALISE' | null => {
  if (
    element.optimisationType === 'optimize' ||
    element.optimisationType === 'optimise'
  ) {
    return 'OPTIMISE'
  }
  if (
    element.optimisationType === 'personalize' ||
    element.optimisationType === 'personalise'
  ) {
    return 'PERSONALISE'
  }
  return null
}

export const getMultipleTemplatesBannerText = (
  controllableElements: DuplicateWorkflowModalElement[]
): string => {
  const hasOptimiseTemplates = controllableElements.some(
    (e) =>
      e.optimisationType === 'optimize' || e.optimisationType === 'optimise'
  )
  const hasPersonalizeTemplates = controllableElements.some(
    (e) =>
      e.optimisationType === 'personalize' ||
      e.optimisationType === 'personalise'
  )

  if (hasOptimiseTemplates && hasPersonalizeTemplates) {
    return 'This workflow contains optimisation and personnalised templates. Update its settings before duplicating.'
  }

  if (hasPersonalizeTemplates) {
    return 'This workflow contains personalization templates. Update its settings before duplicating.'
  }

  // Default to optimisation templates if we cannot infer a personalize/optimise mix.
  return 'This workflow contains optimisation templates. Update its settings before duplicating.'
}
